/** @format */

import {
  Box,
  Typography,
  Modal,
  Paper,
  Divider,
  Button,
  IconButton,
  Tooltip,
} from '@mui/material';
import FilterListOutlinedIcon from '@mui/icons-material/FilterListOutlined';
import CloseIcon from '@mui/icons-material/Close';
import { useTheme } from '@mui/material/styles';
import { useEffect, useState } from 'react';
import dayjs from 'dayjs';

import Parameter from './AppFilter/AppFilter.Parameter';
import DateRangePicker from '../DateRangePicker/DateRangePicker';
import { FILTER_NAMES, useAppContext } from '../../../store/store.app';
import filtericon from '../../../Assets/img/filter-icon.png';
import useCalculateDate, {
  goPastInDate,
} from '../../../hooks/misc/useCalculateDate';
import { isCYHotelSelected } from '../../Library/appfilter.lib';

const modalStyle = {
  position: 'absolute',
  p: 2,
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: { xs: 'calc(100% - 24px)', sm: '500px' },
  maxWidth: '600px',
  maxHeight: 'calc(100vh - 32px)',
  minHeight: 0,
  boxShadow: 24,
  border: 'none',
  borderRadius: 3,
  display: 'flex',
  flexDirection: 'column',
};

function AppFilter({ compact = false }) {
  const theme = useTheme();
  const [filterModal, setFilterModal] = useState(false);

  const [filters, setFilters] = useState({
    parameters: [],
    daterange: null,
  });

  const {
    appStore: { appFilters },
    updateFilters,
  } = useAppContext();

  const DATERANGE = appFilters[FILTER_NAMES.DATERANGE];
  const PARAMETER = appFilters[FILTER_NAMES.PARAMETER];
  const hotelParam = PARAMETER?.find((p) => p.name === 'hotel');
  const selectedHotel = hotelParam?.selected || [];
  const selectedHotelJson = JSON.stringify(selectedHotel);

  const applyFilters = function () {
    updateFilters(FILTER_NAMES.DATERANGE, filters.daterange);
    updateFilters(FILTER_NAMES.PARAMETER, filters.parameters);
    setFilterModal(false);
  };

  useEffect(() => {
    if (!DATERANGE) return;
    const isCY = isCYHotelSelected(appFilters);
    const past90Days = goPastInDate(DATERANGE, isCY);
    updateFilters(FILTER_NAMES.CALC_DATERANGE, past90Days);
  }, [DATERANGE, selectedHotelJson]);

  useEffect(() => {
    if (filterModal) {
      if (
        appFilters[FILTER_NAMES.PARAMETER] &&
        appFilters[FILTER_NAMES.DATERANGE]
      ) {
        setFilters((prev) => ({
          ...prev,
          parameters: appFilters[FILTER_NAMES.PARAMETER],
          daterange: appFilters[FILTER_NAMES.DATERANGE],
        }));
      } else console.error('PARAMETER NOT FOUND: [APP FILTER]');
    }
  }, [filterModal]);

  return (
    <Box sx={{ p: compact ? 0 : 2 }}>
      {compact ? (
        <Tooltip title="Filters">
          <IconButton
            aria-label="Open filters"
            onClick={() => setFilterModal(true)}
            size="small"
            sx={{
              width: 34,
              height: 34,
              color: '#4b5563',
              border: '1px solid #e5e7eb',
              borderRadius: '8px',
              backgroundColor: '#fff',
            }}
          >
            <FilterListOutlinedIcon sx={{ fontSize: 20 }} />
          </IconButton>
        </Tooltip>
      ) : (
        <Box
          component="button"
          aria-label="Open filters"
          onClick={() => setFilterModal(true)}
          sx={{
            display: 'block',
            p: 0,
            border: 0,
            backgroundImage: `url(${filtericon})`,
            backgroundSize: '100%',
            backgroundColor: 'white',
            borderRadius: '2px',
            width: 20,
            height: 20,
            cursor: 'pointer',
          }}
        />
      )}
      <Modal
        open={filterModal}
        sx={{
          backdropFilter: 'blur(2px) saturate(180%)',
          backgroundColor:
            theme.palette.mode === 'dark'
              ? 'rgba(9, 9, 10, 0.75)'
              : 'rgba(14, 121, 184, 0.5)',
        }}
        disableAutoFocus
        onClose={() => setFilterModal(false)}
      >
        <Paper
          role="dialog"
          aria-labelledby="filter-modal-title"
          sx={{ ...modalStyle, p: { xs: 2, sm: 3 } }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'flex-start',
              justifyContent: 'space-between',
              gap: 1,
              mb: { xs: 1.5, sm: 2 },
            }}
          >
            <Box id="filter-modal-title">
              <Typography variant="subtitle2">Filters</Typography>
              <Typography
                sx={{
                  fontSize: { xs: '0.72rem', sm: '11px' },
                  color: 'text.secondary',
                }}
              >
                Apply to all pages
              </Typography>
            </Box>
            <IconButton
              aria-label="Close filters"
              onClick={() => setFilterModal(false)}
              size="small"
              sx={{ mt: -0.75, mr: -0.75 }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
          <Divider />
          <Box sx={{ overflowY: 'auto', minHeight: 0 }}>
            <Filters setFilters={setFilters} filters={filters} />
          </Box>
          <FormButtons
            setFilterModal={setFilterModal}
            applyFilters={applyFilters}
          />
        </Paper>
      </Modal>
    </Box>
  );
}

function Filters({ setFilters, filters }) {
  console.log(filters);
  function handleDateRange(dRange) {
    setFilters((prevState) => ({ ...prevState, daterange: dRange }));
  }

  return (
    <Box sx={{ my: { xs: 1.5, sm: 2 } }}>
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          gap: { xs: 1.5, sm: 1 },
          alignItems: 'stretch',
        }}
      >
        <Parameter setFilters={setFilters} filters={filters} />
        <DateRangePicker
          handleDateRange={handleDateRange}
          defaultDateRange={filters.daterange}
          isCY={isCYHotelSelected({ PARAMETER: filters.parameters })}
          maxDate={new Date()}
          height={'40px'}
          fullWidth
        />
      </Box>
    </Box>
  );
}

const FormButtons = function ({ setFilterModal, applyFilters }) {
  return (
    <Box
      sx={{
        display: 'flex',
        gap: 1,
        justifyContent: 'flex-end',
        borderTop: '1px solid',
        borderColor: 'divider',
        pt: { xs: 1.5, sm: 2 },
        mt: 'auto',
      }}
    >
      <Button
        onClick={() => {
          setFilterModal(false);
        }}
        variant="link"
        size="small"
        sx={{ minWidth: 72, p: '6px 12px' }}
      >
        Close
      </Button>
      <Button
        onClick={applyFilters}
        variant="contained"
        size="small"
        sx={{ minWidth: 72, p: '6px 12px' }}
      >
        Apply filters
      </Button>
    </Box>
  );
};

export default AppFilter;
